/**
 * Admin Announcements — CRUD panel.
 *
 * - Lists all announcements (most recent first) with stats (eligible/views/reads).
 * - Create form with audience picker.
 * - Per-row deactivate toggle + soft delete.
 * - No email trigger (Phase 1 = in-app only, by design).
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Megaphone, Trash2, Power, PowerOff, Plus, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function authHeaders() {
  const token = typeof window !== "undefined" ? window.localStorage.getItem("token") : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const AUDIENCES = [
  { value: "all", label: "All users" },
  { value: "students", label: "Students only" },
  { value: "educators", label: "Educators only" },
  { value: "learning_path", label: "Learning path" },
  { value: "class", label: "One class" },
];

const SEVERITIES = [
  { value: "info", label: "Info (blue)" },
  { value: "success", label: "Success (green)" },
  { value: "warning", label: "Warning (amber)" },
];

export default function AdminAnnouncementsPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    title: "",
    body: "",
    audience_type: "all",
    audience_value: "",
    expires_at: "",
    severity: "info",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [annRes, classesRes] = await Promise.all([
        axios.get(`${API}/admin/announcements`, { headers: authHeaders() }),
        axios.get(`${API}/classes`, { headers: authHeaders() }).catch(() => ({ data: [] })),
      ]);
      setItems(annRes.data?.announcements || []);
      setClasses(classesRes.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role !== "admin") {
      navigate("/dashboard");
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const audienceValueOptions = useMemo(() => {
    if (form.audience_type === "learning_path") {
      return [
        { value: "standard", label: "Standard (high school)" },
        { value: "middle_school", label: "Middle school" },
      ];
    }
    if (form.audience_type === "class") {
      return classes.map((c) => ({ value: c.id, label: c.name }));
    }
    return null;
  }, [form.audience_type, classes]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.title.trim() || !form.body.trim()) {
      setError("Title and body are required.");
      return;
    }
    if ((form.audience_type === "class" || form.audience_type === "learning_path") && !form.audience_value) {
      setError("Please pick the audience value.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        title: form.title.trim(),
        body: form.body.trim(),
        audience_type: form.audience_type,
        audience_value: form.audience_value || null,
        severity: form.severity,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      };
      await axios.post(`${API}/admin/announcements`, payload, { headers: authHeaders() });
      setForm({ title: "", body: "", audience_type: "all", audience_value: "", expires_at: "", severity: "info" });
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not create announcement.");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (a) => {
    try {
      await axios.patch(
        `${API}/admin/announcements/${a.id}`,
        { active: !a.active },
        { headers: authHeaders() }
      );
      await load();
    } catch {
      // no-op; next load will reflect server truth
    }
  };

  const hardDelete = async (a) => {
    if (!window.confirm(`Delete "${a.title}"? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/announcements/${a.id}`, { headers: authHeaders() });
      await load();
    } catch {
      // ignore
    }
  };

  const describeAudience = (a) => {
    const at = a.audience_type;
    if (at === "all") return "All users";
    if (at === "students") return "Students only";
    if (at === "educators") return "Educators only";
    if (at === "learning_path") return `Learning path: ${a.audience_value}`;
    if (at === "class") {
      const c = classes.find((x) => x.id === a.audience_value);
      return `Class: ${c?.name || a.audience_value}`;
    }
    return "—";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-amber-50">
      <header className="bg-white border-b border-amber-200">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="inline-flex items-center gap-1 text-sm text-gray-700 hover:text-gray-900"
            data-testid="admin-ann-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Admin
          </button>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            Signed in as <span className="font-semibold">{user?.name}</span>
            <button
              type="button"
              onClick={onLogout}
              className="ml-2 px-2 py-1 rounded border border-gray-300 text-xs hover:bg-gray-50"
              data-testid="admin-ann-logout"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2 mb-1">
          <Megaphone className="w-7 h-7 text-amber-600" />
          Announcements
        </h1>
        <p className="text-sm text-gray-600 mb-6">
          Broadcast in-app messages to students, educators, a class, or a learning path.
        </p>

        {/* Create form */}
        <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 mb-8">
          <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Plus className="w-5 h-5" /> New announcement
          </h2>
          <form onSubmit={handleSubmit} className="grid gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Title</label>
              <input
                type="text"
                value={form.title}
                maxLength={200}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="e.g. Spring pitch competition opens Monday!"
                data-testid="admin-ann-title-input"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Body</label>
              <textarea
                rows={4}
                value={form.body}
                maxLength={5000}
                onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="What should users know?"
                data-testid="admin-ann-body-input"
                required
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Audience</label>
                <select
                  value={form.audience_type}
                  onChange={(e) => setForm((f) => ({ ...f, audience_type: e.target.value, audience_value: "" }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  data-testid="admin-ann-audience-type"
                >
                  {AUDIENCES.map((a) => (
                    <option key={a.value} value={a.value}>{a.label}</option>
                  ))}
                </select>
              </div>
              {audienceValueOptions && (
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    {form.audience_type === "class" ? "Class" : "Path"}
                  </label>
                  <select
                    value={form.audience_value}
                    onChange={(e) => setForm((f) => ({ ...f, audience_value: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    data-testid="admin-ann-audience-value"
                  >
                    <option value="">— choose —</option>
                    {audienceValueOptions.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Severity</label>
                <select
                  value={form.severity}
                  onChange={(e) => setForm((f) => ({ ...f, severity: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  data-testid="admin-ann-severity"
                >
                  {SEVERITIES.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Expires at <span className="font-normal text-gray-500">(optional — leave blank for no expiration)</span>
              </label>
              <input
                type="datetime-local"
                value={form.expires_at}
                onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value }))}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                data-testid="admin-ann-expires"
              />
            </div>
            {error && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                {error}
              </div>
            )}
            <div>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-amber-600 hover:bg-amber-700 text-white font-semibold text-sm disabled:opacity-50"
                data-testid="admin-ann-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Publish
              </button>
            </div>
          </form>
        </section>

        {/* List */}
        <section>
          <h2 className="text-lg font-bold text-gray-800 mb-3">All announcements</h2>
          {loading ? (
            <div className="text-sm text-gray-500">Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-sm text-gray-500 bg-white border border-dashed border-gray-300 rounded-xl p-6 text-center">
              No announcements yet.
            </div>
          ) : (
            <ul className="space-y-3">
              {items.map((a) => {
                const expired = a.expires_at && new Date(a.expires_at) < new Date();
                return (
                  <li
                    key={a.id}
                    data-testid={`admin-ann-row-${a.id}`}
                    className={`bg-white border rounded-xl p-4 shadow-sm ${
                      !a.active ? "border-gray-200 opacity-70" : "border-amber-200"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-gray-900">{a.title}</span>
                          <span
                            className={`text-[10px] uppercase font-bold tracking-wide px-2 py-0.5 rounded ${
                              a.severity === "warning"
                                ? "bg-amber-100 text-amber-800"
                                : a.severity === "success"
                                ? "bg-emerald-100 text-emerald-800"
                                : "bg-blue-100 text-blue-800"
                            }`}
                          >
                            {a.severity || "info"}
                          </span>
                          {!a.active && (
                            <span className="text-[10px] uppercase font-bold tracking-wide bg-gray-200 text-gray-700 px-2 py-0.5 rounded">
                              Inactive
                            </span>
                          )}
                          {expired && (
                            <span className="text-[10px] uppercase font-bold tracking-wide bg-red-100 text-red-700 px-2 py-0.5 rounded">
                              Expired
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{a.body}</p>
                        <div className="text-xs text-gray-500 mt-2 flex gap-4 flex-wrap">
                          <span><strong>Audience:</strong> {describeAudience(a)}</span>
                          <span>
                            <strong>Reach:</strong> {a.stats?.reads || 0} read / {a.stats?.views || 0} viewed
                            / {a.stats?.eligible || 0} eligible
                          </span>
                          {a.expires_at && (
                            <span><strong>Expires:</strong> {new Date(a.expires_at).toLocaleString()}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => toggleActive(a)}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-semibold border ${
                            a.active
                              ? "border-gray-300 text-gray-700 hover:bg-gray-50"
                              : "border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                          }`}
                          data-testid={`admin-ann-toggle-${a.id}`}
                        >
                          {a.active ? <><PowerOff className="w-3.5 h-3.5" /> Deactivate</> : <><Power className="w-3.5 h-3.5" /> Activate</>}
                        </button>
                        <button
                          type="button"
                          onClick={() => hardDelete(a)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-semibold border border-red-300 text-red-700 hover:bg-red-50"
                          data-testid={`admin-ann-delete-${a.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Delete
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}
